// Copy-the-badge affordances. Two shapes:
//   <BadgeCopyButton>  — shows the live badge image; click opens a popdown
//                        with a preview and HTML/Markdown/JSX copy buttons.
//                        Used on gem cards (pops up) and the detail page (pops down).
//   <BadgeSnippets>    — full picker with previews (the /library/badge page)

import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "@/components/icons";
import {
  BADGE_ALT,
  BADGE_DIMS,
  badgeHtml,
  badgeJsx,
  badgeMarkdown,
  badgePreviewSrc,
  type BadgeSize,
  type BadgeTheme,
} from "@/lib/library-badge";

async function writeClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand?.("copy");
    el.remove();
  }
}

function useCopied() {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  return [copied, setCopied] as const;
}

type CopyFmt = "html" | "md" | "jsx";

const FMT_LABEL: Record<CopyFmt, string> = {
  html: "HTML",
  md: "Markdown",
  jsx: "JSX",
};

function codeFor(slug: string, fmt: CopyFmt, theme: BadgeTheme, size: BadgeSize) {
  if (fmt === "html") return badgeHtml(slug, theme, size);
  if (fmt === "md") return badgeMarkdown(slug, theme, size);
  return badgeJsx(slug, theme, size);
}

/**
 * A self-contained popover that shows the live "Featured" badge image as its
 * trigger, then expands a panel with a larger preview and one-tap copy buttons
 * for HTML / Markdown / JSX. `placement` controls which way the panel opens:
 * "up" for gem cards (opens above the footer), "down" for the detail page.
 */
export function BadgeCopyButton({
  slug,
  className = "",
  stopNavigation = false,
  placement = "up",
  triggerSize = "sm",
}: {
  slug: string;
  className?: string;
  /** Set when the button lives inside a link (the gem cards). */
  stopNavigation?: boolean;
  /** Which direction the popdown panel opens. */
  placement?: "up" | "down";
  /** Size of the badge image shown on the trigger. */
  triggerSize?: BadgeSize;
}) {
  const [open, setOpen] = useState(false);
  const [lastFmt, setLastFmt] = useState<CopyFmt | null>(null);
  const [theme, setTheme] = useState<BadgeTheme>("dark");
  const wrapRef = useRef<HTMLDivElement>(null);
  const td = BADGE_DIMS[triggerSize];

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const stop = (e: React.SyntheticEvent) => {
    if (stopNavigation) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const panel =
    placement === "up"
      ? "absolute bottom-[calc(100%+8px)] left-0 origin-bottom-left"
      : "absolute top-[calc(100%+8px)] left-0 origin-top-left";

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        onPointerDown={stopNavigation ? (e) => e.stopPropagation() : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Show the "${BADGE_ALT}" badge and copy options`}
        className={`group relative inline-flex cursor-pointer items-center gap-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
      >
        <img
          src={badgePreviewSrc(slug, "light", triggerSize)}
          alt={BADGE_ALT}
          height={td.h}
          loading="lazy"
          decoding="async"
          className="block w-auto"
          style={{ height: td.h, maxWidth: "none" }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute left-[calc(100%+8px)] top-1/2 z-40 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-full bg-foreground px-3 py-1.5 text-[11px] font-medium text-background opacity-0 shadow-[0_1px_2px_rgba(20,20,40,0.08),0_8px_20px_-12px_rgba(20,20,40,0.3)] transition-all duration-200 ease-[cubic-bezier(0.2,0,0,1)] group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
        >
          Copy this element
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={`${BADGE_ALT} — copy options`}
          onClick={stop}
          onPointerDown={stopNavigation ? (e) => e.stopPropagation() : undefined}
          className={`z-50 w-[248px] rounded-2xl bg-popover p-3 shadow-[0_1px_2px_rgba(20,20,40,0.06),0_18px_40px_-18px_rgba(20,20,40,0.4)] ring-1 ring-border/70 ${panel}`}
        >
          {/* Light / Dark tab switch */}
          <div
            role="tablist"
            aria-label="Badge theme"
            className="grid grid-cols-2 gap-1 rounded-full bg-muted/50 p-0.5 ring-1 ring-border/50"
          >
            {(["dark", "light"] as const).map((t) => {
              const active = theme === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={(e) => {
                    stop(e);
                    setTheme(t);
                    setLastFmt(null);
                  }}
                  className={`inline-flex min-h-[28px] items-center justify-center rounded-full text-[11px] font-medium capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    active
                      ? "bg-foreground text-background shadow-[0_1px_2px_rgba(20,20,40,0.16)]"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {/* Preview of exactly what they'll embed, on a surface matching the theme */}
          <div
            className={`mt-2.5 flex items-center justify-center rounded-xl px-3 py-3 ring-1 ring-border/50 ${
              theme === "dark" ? "bg-neutral-900" : "bg-white"
            }`}
          >
            <img
              src={badgePreviewSrc(slug, theme, "md")}
              alt={BADGE_ALT}
              height={BADGE_DIMS.md.h}
              loading="eager"
              decoding="async"
              className="block h-[42px] w-auto max-w-full"
            />
          </div>
          <p className="mt-2 text-center text-[11px] leading-relaxed text-muted-foreground">
            Paste this on your site to link back to this gem.
          </p>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {(["html", "md", "jsx"] as const).map((fmt) => {
              const copied = lastFmt === fmt;
              return (
                <button
                  key={fmt}
                  type="button"
                  onClick={(e) => {
                    stop(e);
                    void writeClipboard(codeFor(slug, fmt, theme, "md"));
                    setLastFmt(fmt);
                  }}
                  className="inline-flex min-h-[32px] items-center justify-center gap-1 rounded-full text-[11px] font-medium text-muted-foreground ring-1 ring-border/60 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Copy ${FMT_LABEL[fmt]} snippet`}
                >
                  {copied ? (
                    <Check aria-hidden className="h-3 w-3" />
                  ) : (
                    <Copy aria-hidden className="h-3 w-3" />
                  )}
                  {copied ? "Copied" : FMT_LABEL[fmt]}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SnippetBlock({ title, code }: { title: string; code: string }) {
  const [copied, setCopied] = useCopied();
  return (
    <div className="rounded-2xl bg-card p-4 ring-1 ring-border/60">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium">{title}</h3>
        <button
          type="button"
          onClick={() => {
            void writeClipboard(code);
            setCopied(true);
          }}
          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground ring-1 ring-border/60 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all rounded-xl bg-muted/50 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
        {code}
      </pre>
    </div>
  );
}

export function BadgeSnippets({ slug }: { slug: string }) {
  const [theme, setTheme] = useState<BadgeTheme>("dark");
  const [size, setSize] = useState<BadgeSize>("md");
  const dims = BADGE_DIMS[size];

  const option = (active: boolean) =>
    `inline-flex min-h-[32px] items-center rounded-full px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active ? "bg-foreground text-background" : "text-muted-foreground ring-1 ring-border/60 hover:bg-muted/60"
    }`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Theme</span>
        <button type="button" className={option(theme === "dark")} onClick={() => setTheme("dark")}>
          Dark
        </button>
        <button type="button" className={option(theme === "light")} onClick={() => setTheme("light")}>
          Light
        </button>
        <span className="ml-3 text-xs uppercase tracking-wide text-muted-foreground">Size</span>
        <button type="button" className={option(size === "md")} onClick={() => setSize("md")}>
          Regular
        </button>
        <button type="button" className={option(size === "sm")} onClick={() => setSize("sm")}>
          Small
        </button>
      </div>

      <div className="flex items-center justify-center rounded-2xl bg-muted/40 p-8 ring-1 ring-border/60">
        <img
          src={badgePreviewSrc(slug, theme, size)}
          alt={BADGE_ALT}
          height={dims.h}
          style={{ height: dims.h, width: "auto" }}
          className="max-w-full"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <SnippetBlock title="HTML" code={badgeHtml(slug, theme, size)} />
        <SnippetBlock title="Markdown" code={badgeMarkdown(slug, theme, size)} />
        <SnippetBlock title="JSX" code={badgeJsx(slug, theme, size)} />
      </div>
    </div>
  );
}
